import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { AriaAttr, EventType, KeyType } from 'constants/enums'
import { dispatchAnalyticsFormSubmissionEvent } from 'utils/analytics'
import { ComponentBase } from 'utils/abstract-classes/component-base'
import { HeaderStore } from './store'

const {
    header: { search: classNames }
} = CLASS_NAME

export class Search extends ComponentBase {
    private toggle: HTMLButtonElement
    private input: HTMLInputElement
    private form: HTMLFormElement
    private submit: HTMLButtonElement

    public constructor(private wrapper: HTMLDivElement) {
        super()

        this.initToggle()
        this.initInput()
        this.initForm()
        this.initSubmit()

        if (!this.toggle || !this.input || !this.form || !this.submit) {
            this.wrapper.remove()
            if (this.toggle) {
                this.toggle.remove()
            }
            if (this.input) {
                this.input.remove()
            }
            if (this.form) {
                this.form.remove()
            }
            if (this.submit) {
                this.submit.remove()
            }
            return
        }

        // Bind Event Listeners
        this.toggle.addEventListener(EventType.CLICK, () =>
            this.openSearchForm()
        )
        this.toggle.addEventListener(EventType.KEYDOWN, (e) => {
            this.previousElementFocus(e)
        })
        this.input.addEventListener(EventType.KEYDOWN, (e) => {
            this.trackSearchFormInputKeyEvents(e)
        })
        this.form.addEventListener(EventType.SUBMIT, (): void => {
            dispatchAnalyticsFormSubmissionEvent('Search', this.input, true)
        })
        this.submit.addEventListener(EventType.KEYDOWN, (e: KeyboardEvent) => {
            this.trackSubmitSearchButtonKeyEvents(e)
        })
        window.addEventListener(EventType.CLICK, (e: PointerEvent) => {
            const target = e?.target as HTMLElement

            if (
                !(
                    target === this.toggle ||
                    target.closest('button') === this.toggle ||
                    target === this.input ||
                    target.closest('input') === this.input ||
                    target === this.submit ||
                    target.closest('button') === this.submit
                )
            ) {
                this.closeSearchForm()
            }
        })
        this.input.addEventListener(EventType.CLICK, (e) => {
            e.preventDefault()
        })
        HeaderStore.getInstance().search = this
    }

    /**
     * Initialize toggle button element
     */
    private initToggle(): void {
        const toggle = this.getElement<HTMLButtonElement>(
            `.${classNames.toggle}`,
            this.wrapper
        )
        if (!toggle) {
            return
        }

        this.toggle = toggle
    }

    /**
     * Initialize input element
     */
    private initInput(): void {
        const input = this.getElement<HTMLInputElement>(
            `#${classNames.inputId}`,
            this.wrapper
        )
        if (!input) {
            return
        }

        this.input = input
        return
    }

    /**
     * Initialize form element
     */
    private initForm(): void {
        const form = this.getElement<HTMLFormElement>(
            `.${classNames.form}`,
            this.wrapper
        )
        if (!form) {
            return
        }
        this.form = form
        return
    }

    /**
     * Initialize submit button element
     */
    private initSubmit(): void {
        const submit = this.getElement<HTMLButtonElement>(
            `.${classNames.submit}`,
            this.wrapper
        )
        if (!submit) {
            return
        }
        this.submit = submit
        return
    }

    /**
     * add classes for open state
     * and toggle aria to inform form is open
     * manually set focus on input (faking focus order)
     * CSS dependency
     */
    private openSearchForm(): void {
        this.toggle.classList.add(classNames.hiddenButton)

        this.toggle.setAttribute(AriaAttr.EXPANDED, 'true')
        this.form.classList.remove(classNames.hiddenForm)
        this.input.focus()
    }

    /**
     * add classes for closed state
     * and toggle aria to inform form is closed
     * manually return focus to the element that triggered opening the form
     * (faking focus order)
     * CSS dependency
     *
     * @param {boolean} setFocus optionally focus the search button
     */
    private closeSearchForm(setFocus?: boolean): void {
        this.toggle.classList.remove(classNames.hiddenButton)
        this.toggle.setAttribute(AriaAttr.EXPANDED, 'false')
        this.form.classList.add(classNames.hiddenForm)
        if (setFocus) {
            this.toggle.focus()
        }
    }

    /**
     * When shift tab focus the previous element button
     * Main Sections menu control button
     *
     * @param {KeyboardEvent} e
     */
    private previousElementFocus(e: KeyboardEvent): void {
        if (e.key === KeyType.TAB && e.shiftKey) {
            const mainMenuCtrl = this.getElement<HTMLButtonElement>(
                `[${ATTR_NAME.header.menu.target}='${CLASS_NAME.header.mainMenu._}']`
            )
            if (mainMenuCtrl) {
                e.preventDefault()
                mainMenuCtrl.focus()
                this.closeSearchForm(false)
            }
            return
        }
    }

    /**
     * handle closing menu when clicking escape on search input
     *
     * @param {KeyboardEvent} e
     */
    private trackSearchFormInputKeyEvents(e: KeyboardEvent): void {
        this.previousElementFocus(e)

        switch (e.key) {
            case KeyType.ESC:
            case KeyType.ESC_IE:
                this.closeSearchForm(true)
                return
            default:
                return
        }
    }

    /**
     * handle closing menu when clicking escape on search button
     *
     * @param {KeyboardEvent} e
     */
    private trackSubmitSearchButtonKeyEvents(e: KeyboardEvent): void {
        switch (e.key) {
            case KeyType.ESC:
            case KeyType.ESC_IE:
                this.closeSearchForm(true)
                return
            case KeyType.TAB:
                if (e.shiftKey) {
                    return
                }
                // when tabbing forward and leaving form: close it
                this.closeSearchForm(false)
                return
            default:
                return
        }
    }

    public focus(): void {
        // Open status
        if (this.toggle.classList.contains(classNames.hiddenButton)) {
            this.input.focus()
        } else {
            this.toggle.focus()
        }
    }
}
